import { Drawer, Group, Title } from "@mantine/core"
import { zodResolver } from "@mantine/form"
import { useDisclosure } from "@mantine/hooks"
import { IconPlus } from "@tabler/icons-react"
import {
  SortingState,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table"
import { Button } from "@tfg/ui"
import { useState } from "react"
import { z } from "zod"

import { AdminResponses } from "@tfg/types/endpoints/admin"
import { OperationChain } from "@tfg/types/schemas/enum"

import { Table } from "~/components/table/Table"

import { FormFieldConfig, TypesForm } from "../../components/TypesForm"

import { useColumns } from "./columns"
import { useCreateProductTypeMutation } from "./hooks"

type Props = {
  productTypes: AdminResponses.ProductTypes[]
  productOpChains: OperationChain[]
}

export function ProductTypesAdmin({ productTypes, productOpChains }: Props) {
  const columns = useColumns()

  const [sorting, setSorting] = useState<SortingState>([
    { id: "name", desc: true },
  ])
  const [drawerOpen, drawerActions] = useDisclosure(false)

  const table = useReactTable({
    data: productTypes,
    columns,
    state: {
      sorting,
    },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  })

  const { mutate: createProductType } = useCreateProductTypeMutation({
    onSuccess: drawerActions.close,
  })

  const reactFormConfig = {
    validate: zodResolver(
      z.object({
        name: z.string().min(1),
        operationChainId: z.string().uuid(),
        ecobalyseId: z.string().nullable().optional(),
      })
    ),
    initialValues: {
      name: "",
      operationChainId: productOpChains[0].id,
      ecobalyseId: "",
    },
  }

  const fields: FormFieldConfig[] = [
    {
      type: "text",
      name: "name",
      label: "Key",
      placeholder: "Product Type Key",
    },
    {
      type: "select",
      name: "operationChainId",
      label: "Operation Chain",
      placeholder: "Product Type Operation Chain",
      options: productOpChains.map((opChain) => ({
        label: opChain.name,
        value: opChain.id,
      })),
    },
    {
      type: "text",
      name: "ecobalyseId",
      label: "Ecobalyse Id",
      placeholder: "Ecobalyse Id",
    },
  ]

  return (
    <>
      <Group justify="right">
        <Button onClick={drawerActions.open} icon={<IconPlus />}>
          New Product Type
        </Button>
      </Group>
      <Drawer
        position="right"
        opened={drawerOpen}
        onClose={() => drawerActions.close()}
        title={<Title order={2}>Create Product Type</Title>}
        size="lg"
      >
        <TypesForm
          fields={fields}
          submitLabel="Create"
          formConfig={reactFormConfig}
          mutate={createProductType}
        />
      </Drawer>
      <Table table={table} pagination={false} />
    </>
  )
}
